import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as css from 'styles';
import styled from '@emotion/styled';
import ReadMore from 'read-more-react';

import Layout from 'layouts/Base';
import { LayoutGrid } from 'layouts/Default';
import { Meta, Img, Link, Grid, Button, CenterContent, MobileOnly, LargerOnly, FadeIn } from 'components';
import ArtworkGrid from 'app/browse/ArtworkGrid';
import defaultImg from 'images/avatar-blank.svg';
import Follow from './ArtistFollow';
import NewArtistSticker from './ArtistNew';

import { ArticlesGrid } from 'app/articles/Articles';
import ArticleThumbnail from 'app/articles/ArticleThumbnail';
import Share from 'app/articles/Share';
import { formatRelativeWithOptions } from 'date-fns/esm/fp';
import arraySortBy from 'utils/arraySortBy';
import { artworks } from 'redux/models';
import Message from './ArtistMessage';

const Profile = () => {
  const states = ['Success', 'Error'];
  const { location } = useSelector((store) => store);
  const {
    data: { user, artist_artworks, artist_articles = [], work_lists = [] },
    status,
  } = useSelector((store) => store.profile);

  useEffect(
    (args) => {
      if (status === 'Success') window.prerenderReady = true;
    },
    [status]
  );

  useEffect(() => {
    location.kind === 'push' && window.scrollTo(0, 0);
  }, []);

  const columns = '1fr 1fr';
  const image_height = window.innerWidth > css.breakpoints.m ? '600px' : '350px';
  const larger_image = user.profile_image && user.profile_image.w800 && user.profile_image.w800.url;
  const mobile_image = user.profile_image && user.profile_image.w800 && user.profile_image.w800.url;

  // Collect all the artwork ids in the lists - is used below to filter remaining artworks
  const work_lists_ids = work_lists.reduce((array, list) => [...array, ...list.artwork_ids], []);

  const work_lists_sorted = work_lists.length ? work_lists.sort(arraySortBy('name', 'desc')) : [];

  const display_lists = work_lists.length
    ? [
        // Map the artworks to the correct list
        ...work_lists_sorted
          .filter((list) => list.artwork_ids && list.artwork_ids.length)
          .map((list) => ({
            ...list,
            artworks: artist_artworks
              .filter((artwork) => list.artwork_ids.includes(artwork.id))
              .sort(
                (a, b) =>
                  list.artwork_ids.findIndex((aP) => aP === a.id) - list.artwork_ids.findIndex((bP) => bP === b.id)
              ),
          })),
        // Place all remaining artworks in the 'default list'
        {
          name: `More from ${user.first_name}`,
          artworks: artist_artworks.filter((artwork) => !work_lists_ids.includes(artwork.id)),
        },
      ]
    : [
        // The default list if no work_lists have been created
        {
          name: `${user.first_name}'s Work`,
          artworks: artist_artworks,
        },
      ];

  const header_articles = artist_articles.filter((article) => article.category.slug === 'inside-the-studio');
  const footer_articles = artist_articles.filter((article) => article.category.slug !== 'inside-the-studio');

  return {
    Success: (
      <>
        <Layout style={{ paddingTop: 0 }}>
          <Meta
            title={`${user.full_name}`}
            description={`View ${user.full_name}'s collection. Directly support artists when you try or buy on Art in Res`}
            type="profile"
            image={user.profile_image && user.profile_image.w800 && user.profile_image.w800.url}
            fragment="!"
          />
          <LayoutGrid style={{ paddingTop: 0, position: 'relative' }}>
            <ProfileHeader>
              <h1>{user.full_name}</h1>

              <div style={{ margin: '1em 0 2em 0' }}>
                <Follow id={user.id} />
                <Message to={`/messages/${user.slug || user.id}`} />
              </div>
            </ProfileHeader>

            <ProfileGrid>
              <ImageWrapper>
                <Img
                  key={location.payload.id + 'img'}
                  src={
                    user.profile_image
                      ? user.profile_image.w800
                        ? user.profile_image.w800.url
                        : user.profile_image.url
                      : defaultImg
                  }
                  square
                  style={{
                    background: css.color.offwhitedark,
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </ImageWrapper>

              <ProfileBio>
                {user.birth_year || user.address_city ? (
                  <div style={{ padding: '0 0 1em' }}>
                    <div
                      style={{
                        fontSize: '1.5em',
                        lineHeight: '1.4em',
                      }}
                    >
                      <div>
                        {user.birth_year && <div>Born {user.birth_year}</div>}
                        {user.address_city && (
                          <div>
                            Lives and works in {user.address_city}, {user.address_state}
                          </div>
                        )}
                        {user.pronouns && <small>Pronouns: {user.pronouns}</small>}
                      </div>
                    </div>
                  </div>
                ) : null}
                <div
                  style={{
                    whiteSpace: 'pre-line',
                    lineHeight: '1.4em',
                    margin: '1em 0 -0em 0em',
                    maxWidth: '600px',
                  }}
                >
                  {user.biography ? (
                    <>
                      <MobileOnly>
                        <ReadMore
                          min={300}
                          ideal={375}
                          max={450}
                          text={user.biography || `${user.first_name || 'This artist'} is yet to write a biography.`}
                          readMoreText={
                            <span
                              style={{
                                fontWeight: 'bold',
                                cursor: 'pointer',
                              }}
                            >
                              Read more...
                            </span>
                          }
                        />
                      </MobileOnly>
                      <LargerOnly>
                        {user.biography || `${user.first_name || 'This artist'} is yet to write a biography.`}
                      </LargerOnly>
                    </>
                  ) : (
                    <div style={{ maxWidth: '350px' }}>
                      {user.first_name || 'This artist'} is yet to write a biography. If you would like to know more
                      about them or their work <br />
                      <Link to={`/messages/${user.slug || user.id}`}>
                        <span style={{ textDecoration: 'underline' }}>
                          Ask&nbsp;{user.first_name}
                          &nbsp;a&nbsp;Question
                        </span>
                      </Link>
                    </div>
                  )}
                </div>
              </ProfileBio>
            </ProfileGrid>
          </LayoutGrid>

          {header_articles.length ? (
            <div
              style={{
                background: css.color.offwhitedark,
                margin: '2em 0',
              }}
            >
              <div
                style={{
                  maxWidth: '90vw',
                  margin: '0 auto',
                  padding: '3em 0',
                }}
              >
                <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
                  <ArticleGrid gap="5em">
                    {header_articles.map((article, i) => (
                      <ArticleThumbnail index={i} key={article.slug} {...article} />
                    ))}
                  </ArticleGrid>
                </div>
              </div>
            </div>
          ) : null}

          <LayoutGrid style={{ paddingTop: 0 }}>
            <div style={{ marginBottom: '2em' }}>
              {artist_artworks.length ? (
                <div>
                  {display_lists.map((list, i) =>
                    list.artworks.length ? (
                      <div key={i} style={{ paddingTop: '2em' }}>
                        <div
                          style={{
                            paddingLeft: '0em',
                            margin: '0 auto',
                            maxWidth: '700px',
                          }}
                        >
                          {list.slug ? (
                            <Grid
                              columns="1fr 30px"
                              columnsMobile="1fr 30px"
                              style={{
                                alignItems: 'flex-end',
                                margin: '1em 0',
                              }}
                            >
                              <Link to={`/lists/${list.slug}`}>
                                <h2
                                  style={{
                                    textAlignX: 'center',
                                    textTransform: 'capitalize',
                                    fontSize: '2em',
                                    lineHeight: '1.2em',
                                    marginBottom: 0,
                                  }}
                                  id={list.name}
                                >
                                  {list.name}
                                </h2>
                              </Link>
                              <div style={{ paddingBottom: '0.5em' }}>
                                <Share
                                  url={`${document.location.origin}/lists/${list.slug}`}
                                  text={`${list.name} by ${user.full_name}`}
                                  image={list.artworks && list.artworks[0] && list.artworks[0].main_image.w800.url}
                                  color={css.color.offwhitedark3}
                                />
                              </div>
                            </Grid>
                          ) : (
                            <h2
                              style={{
                                textAlign: 'center',
                                textTransform: 'capitalize',
                                fontSize: '2em',
                                lineHeight: '1.2em',
                              }}
                              id={list.name}
                            >
                              {list.name}
                            </h2>
                          )}
                          {list.description && (
                            <p
                              style={{
                                textAlign: 'left',
                                textIndent: '0em',
                                lineHeight: '1.4em',
                                margin: '0 auto',
                                marginTop: '0em',
                                marginBottom: '2em',
                                maxWidth: '700px',
                                paddingTop: '1em',
                                borderTop: `1px dotted ${css.color.offwhitedark3}`,
                              }}
                            >
                              <span style={{ textTransform: 'uppercase' }}>{list.description.slice(0, 1)}</span>
                              {list.description.slice(1, -1)}
                            </p>
                          )}
                        </div>

                        <div style={{ padding: '1em 0' }}>
                          <ArtworkGrid
                            limit={200}
                            columns="1fr 1fr 1fr"
                            showTitles={true}
                            artworks={list.artworks}
                            status="Success"
                          />
                        </div>
                      </div>
                    ) : null
                  )}
                </div>
              ) : (
                <div>
                  <h3 style={{ textAlign: 'center' }}>{user.first_name || 'This artist'} is yet to list their work.</h3>
                  <ArtworkGrid limit={4} status="Pending" />
                </div>
              )}
            </div>

            <div
              style={{
                maxWidth: '440px',
                margin: '0 auto',
                textAlign: 'center',
                padding: '3em 2em',
              }}
            >
              <div style={{ marginBottom: '1.5em', lineHeight: '1.1em' }}>
                <h2>Chat with {user.first_name}</h2>
                <p style={{ fontSize: '1.4em', lineHeight: '1.1em' }}>
                  We encourage users to talk directly with artists. Learn about {user.first_name} and their process.
                </p>
              </div>
              <Link to={`/messages/${user.slug || user.id}`}>
                <Button small outlined tabIndex="-1" style={{ paddingLeft: '2em', paddingRight: '2em' }}>
                  Ask a question
                </Button>
              </Link>
            </div>
          </LayoutGrid>

          {footer_articles.length ? (
            <div style={{ background: css.color.offwhitedark }}>
              <div
                style={{
                  maxWidth: '90vw',
                  margin: '0 auto',
                  padding: '3em 0',
                }}
              >
                <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
                  {/* <h3 style={{ textAlign: 'center' }}>Read more from {article.category.title}</h3> */}
                  <h2
                    style={{
                      textAlign: 'center',
                      marginBottom: '2em',
                    }}
                  >
                    Read more articles featuring {user.first_name}
                  </h2>
                  <ArticleGrid gap="5em">
                    {footer_articles.map((article, i) => (
                      <ArticleThumbnail index={i} key={article.slug} {...article} />
                    ))}
                  </ArticleGrid>
                </div>
              </div>
            </div>
          ) : null}
        </Layout>
      </>
    ),
    Pending: (
      <Layout style={{ paddingTop: 0 }}>
        <LayoutGrid style={{ paddingTop: 0 }}>
          <ProfileHeader>
            <h1
              style={{
                height: '1.2em',
                background: css.color.offwhitedark,
                width: '60%',
              }}
            >
              &nbsp;
            </h1>
            <div
              style={{
                height: '2em',
                background: css.color.offwhitedark,
                width: '20%',
                margin: '0 auto',
                marginTop: '1em',
                marginBottom: '1em',
              }}
            ></div>
          </ProfileHeader>

          <ProfileGrid>
            <ImageWrapper>
              <Img key={location.payload.id + 'fakeimg'} errormessage=" " square style={profileStyle} />
            </ImageWrapper>

            <ProfileBio>
              <div
                style={{
                  fontStyle: 'italic',
                  fontSize: '1.5em',
                  lineHeight: '1.4em',
                }}
              >
                <div
                  style={{
                    height: '1.4em',
                    marginBottom: '4px',
                    background: css.color.offwhitedark,
                    width: '40%',
                  }}
                ></div>
                <div
                  style={{
                    height: '1.4em',
                    marginBottom: '4px',
                    background: css.color.offwhitedark,
                    width: '70%',
                  }}
                ></div>
              </div>

              <div style={{ lineHeight: '1.4em', marginTop: '2em' }}>
                <div
                  style={{
                    height: '1em',
                    marginBottom: '4px',
                    background: css.color.offwhitedark,
                    width: '90%',
                  }}
                ></div>
                <div
                  style={{
                    height: '1em',
                    marginBottom: '4px',
                    background: css.color.offwhitedark,
                    width: '80%',
                  }}
                ></div>
                <div
                  style={{
                    height: '1em',
                    marginBottom: '4px',
                    background: css.color.offwhitedark,
                    width: '90%',
                  }}
                ></div>
                <div
                  style={{
                    height: '1em',
                    marginBottom: '4px',
                    background: css.color.offwhitedark,
                    width: '85%',
                  }}
                ></div>
                <div
                  style={{
                    height: '1em',
                    marginBottom: '4px',
                    background: css.color.offwhitedark,
                    width: '80%',
                  }}
                ></div>
                <div
                  style={{
                    height: '1em',
                    marginBottom: '4px',
                    background: css.color.offwhitedark,
                    width: '95%',
                  }}
                ></div>
                <div
                  style={{
                    height: '1em',
                    marginBottom: '4px',
                    background: css.color.offwhitedark,
                    width: '75%',
                  }}
                ></div>
                <div
                  style={{
                    height: '1em',
                    marginBottom: '4px',
                    background: css.color.offwhitedark,
                    width: '85%',
                  }}
                ></div>
                <div
                  style={{
                    height: '1em',
                    marginBottom: '4px',
                    background: css.color.offwhitedark,
                    width: '30%',
                  }}
                ></div>
              </div>
            </ProfileBio>
          </ProfileGrid>

          <div style={{ paddingTop: '4em' }}>
            <h3
              style={{
                height: '2em',
                textAlign: 'center',
                background: css.color.offwhitedark,
                width: '35%',
                margin: '0 auto',
                marginBottom: '1em',
              }}
            >
              &nbsp;
            </h3>
            <ArtworkGrid limit={4} status="Pending" />
          </div>
        </LayoutGrid>
      </Layout>
    ),
    Error: (
      <Layout>
        <CenterContent>
          <h2>Profile was not found :O</h2>
          <Link to="/">
            <Button small outlined tabIndex="-1">
              View the collection
            </Button>
          </Link>
        </CenterContent>
      </Layout>
    ),
  }[states.includes(status) ? status : 'Pending'];
  // }['Pending']
};

const ProfileHeader = styled('div')({
  position: 'relative',
  paddingTop: '1em',
  paddingBottom: '2em',
  zIndex: 2,
  textAlign: 'center',

  [css.breakpoint.m_down]: {
    paddingBottom: '1em',
  },

  h1: {
    display: 'inline-block',
    marginBottom: '0',
    fontSize: '5em',
    lineHeight: '1.1em',

    [css.breakpoint.m_down]: {
      fontSize: '2.5em',
    },
  },
});

const ProfileBio = styled('div')({
  padding: '0 2em 0 0',
  [css.breakpoint.m_down]: {
    padding: '0 1em',
    marginTop: '-1em',
  },
});

const profileStyle = {
  background: css.color.offwhitedark,
  height: '100%',
  objectFit: 'cover',
};

const ProfileGrid = styled(Grid)({
  gap: '4em',
  [css.breakpoint.m_down]: {
    gap: '4em',
  },
});

const ImageWrapper = styled('div')({
  position: 'relative',
  // paddingTop: '1em',
  '&:before': {
    width: '500px',
    height: '500px',
    background: css.color.white,
    position: 'absolute',
    top: '4em',
    left: '4em',
  },
  [css.breakpoint.m_down]: {
    margin: '0 -1em 0',
    paddingTop: 0,
  },
});

const ArticleGrid = styled(Grid)({});

export default Profile;
